import { z } from 'zod';
/**
 * Client for the real, live-verified backend auth endpoints
 * (meridian-nissei-auth-foundation). Every shape here matches the actual
 * request/response contract confirmed during BRD 01-04's live testing
 * against real Cognito + Postgres infrastructure -- not assumed.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL;

export interface LoginTokensResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface LoginChallengeResponse {
  /**
   * Confirmed live: /auth/login's underlying initiateAuth() forwards
   * whatever challengeName Cognito actually returns, generically -- it
   * is NOT restricted to just SOFTWARE_TOKEN_MFA/NEW_PASSWORD_REQUIRED.
   * A user whose password is already permanent (CONFIRMED status) but
   * has no MFA enrolled (e.g. after an admin resets their MFA) gets
   * MFA_SETUP directly from THIS endpoint -- there is no
   * NEW_PASSWORD_REQUIRED step to go through first in that case. This
   * was a real, live-reproduced bug: the type here previously excluded
   * MFA_SETUP, so that real response silently fell through to the
   * NEW_PASSWORD_REQUIRED branch in the login handler.
   */
  challengeName: 'SOFTWARE_TOKEN_MFA' | 'NEW_PASSWORD_REQUIRED' | 'MFA_SETUP';
  session: string;
  userId: string;
}

export type LoginResponse = LoginTokensResponse | LoginChallengeResponse;

/**
 * completeNewPassword() and completeMfaSetupVerify() can each return
 * either real tokens or a follow-up challenge (confirmed live: for this
 * pool's MfaConfiguration=ON, completing NEW_PASSWORD_REQUIRED for an
 * MFA-unenrolled user does NOT return tokens -- it immediately chains
 * into MFA_SETUP instead). MFA_SETUP is the only real follow-up challenge
 * this backend can currently produce from that step.
 */
export interface MfaSetupChallengeResponse {
  challengeName: 'MFA_SETUP';
  session: string;
  userId: string;
}

export type NewPasswordResponse = LoginTokensResponse | MfaSetupChallengeResponse;

export interface ApiError {
  error: string;
  message?: string;
}

function isChallengeResponse(
  body: LoginResponse,
): body is LoginChallengeResponse {
  return 'challengeName' in body;
}

function isMfaSetupChallenge(
  body: NewPasswordResponse,
): body is MfaSetupChallengeResponse {
  return 'challengeName' in body;
}

export { isChallengeResponse, isMfaSetupChallenge };

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new Error(`Unexpected response (status ${res.status})`);
  }
  if (!res.ok) {
    const err = body as ApiError;
    throw new Error(err.message ?? err.error ?? `Request failed (status ${res.status})`);
  }
  return body as T;
}

export async function login(
  username: string,
  password: string,
): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  return parseJsonOrThrow<LoginResponse>(res);
}

export async function verifyMfa(
  challengeName: 'SOFTWARE_TOKEN_MFA',
  session: string,
  code: string,
  userId: string,
): Promise<LoginTokensResponse> {
  const res = await fetch(`${API_BASE}/auth/mfa`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ challengeName, session, code, userId }),
  });
  return parseJsonOrThrow<LoginTokensResponse>(res);
}

/** Completes a NEW_PASSWORD_REQUIRED challenge (first login with a
 * temporary password). See NewPasswordResponse's doc comment: this may
 * return real tokens OR a follow-up MFA_SETUP challenge. */
export async function completeNewPassword(
  session: string,
  userId: string,
  newPassword: string,
): Promise<NewPasswordResponse> {
  const res = await fetch(`${API_BASE}/auth/new-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session, userId, newPassword }),
  });
  return parseJsonOrThrow<NewPasswordResponse>(res);
}

export interface MfaSetupBeginResponse {
  secretCode: string;
  session: string;
}

/** Step 1 of first-time MFA enrollment: get a fresh TOTP secret to
 * render as a QR code. Authorized purely by the MFA_SETUP challenge's
 * session -- the user has no access token yet at this point. */
export async function beginMfaSetup(session: string): Promise<MfaSetupBeginResponse> {
  const res = await fetch(`${API_BASE}/auth/mfa-setup/begin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session }),
  });
  return parseJsonOrThrow<MfaSetupBeginResponse>(res);
}

/** Step 2 of first-time MFA enrollment: submit the code from the user's
 * authenticator app to complete enrollment and receive real tokens. */
export async function verifyMfaSetup(
  session: string,
  code: string,
  userId: string,
): Promise<LoginTokensResponse> {
  const res = await fetch(`${API_BASE}/auth/mfa-setup/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session, code, userId }),
  });
  return parseJsonOrThrow<LoginTokensResponse>(res);
}

/**
 * BRD 06 UC-2: real POST /auth/logout (requireAuth on the backend).
 *
 * Note (documented, not fixed by this call): the backend does not
 * immediately revoke the access token server-side on logout -- calling
 * this is still worth doing (it's the real, intended endpoint), but the
 * caller must not rely on it for anything security-critical. Callers
 * should clear local tokens regardless of whether this call succeeds.
 */
export async function logout(accessToken: string): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      throw new Error(`Request failed (status ${res.status})`);
    }
    const err = body as ApiError;
    throw new Error(err.message ?? err.error ?? `Request failed (status ${res.status})`);
  }
}

/**
 * BRD 06 UC-8: real POST /auth/forgot-password.
 *
 * Deliberately always resolves with the same generic message, even for
 * a Cognito failure (UserNotFoundException included) -- the backend
 * itself never distinguishes this in the HTTP response, to avoid
 * confirming or denying an account's existence. Only a genuine network/
 * validation failure (missing username, 400) throws here.
 */
export async function forgotPassword(username: string): Promise<{ message: string }> {
  const res = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username }),
  });
  return parseJsonOrThrow(res);
}

/**
 * BRD 06 UC-8: real POST /auth/reset-password. Unlike forgot-password,
 * this DOES distinguish real failure reasons -- the backend returns
 * three distinct 400 messages (expired/invalid code, weak password,
 * reused password) plus a 502 fallback; this function surfaces
 * whichever one comes back rather than a generic message.
 */
export async function resetPassword(
  username: string,
  code: string,
  newPassword: string,
): Promise<{ message: string }> {
  const res = await fetch(`${API_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, code, newPassword }),
  });
  return parseJsonOrThrow(res);
}

// Verified against backend origin/release auth refresh route and Cognito refresh service.
const refreshResponse = z.object({
  accessToken: z.string().min(1), idToken: z.string().min(1),
  expiresIn: z.number().finite().positive(), refreshToken: z.string().min(1).optional(),
});
export type RefreshTokensResponse = z.infer<typeof refreshResponse>;
export async function refreshTokens(refreshToken: string): Promise<RefreshTokensResponse> {
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  return refreshResponse.parse(await parseJsonOrThrow<unknown>(response));
}
